import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import DashboardCard from './DashboardCard';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatUtcDateTime } from '../../utils/dateFormatUtils';
import { BRAND } from '../../theme/brandTokens';
import useBrandPalette from '../../theme/useBrandPalette';

const MEDAL_GREY = '#a0a0a0';
const MEDAL_BRONZE = '#cd7f32';
const PODIUM_SIZE = 3;

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  py: 1.25,
  '&:not(:last-child)': {
    borderBottom: '1px solid',
    borderColor: BRAND.lineSoft,
  },
};

const RECENT_LIST_SX = {
  maxHeight: 440,
  overflowY: 'auto',
};

const EmptyText = ({ text }) => (
  <Box sx={{ py: 3, textAlign: 'center' }}>
    <Typography variant="body2" sx={{ color: BRAND.textMuted }}>{text}</Typography>
  </Box>
);

const normalizeEnumKey = (value) => {
  const raw = String(value || '');
  if (!raw) return raw;
  return raw.charAt(0).toLowerCase() + raw.slice(1);
};

export const TopServicesCard = ({ services, isLoading, hasError, onRetry }) => {
  const { t } = useTranslation();

  return (
    <DashboardCard
      id="dashboard-top-services"
      title={t('dashboard.cards.topServices')}
      subtitle={t('dashboard.cards.topServicesSubtitle')}
      isLoading={isLoading}
      hasError={hasError}
      onRetry={onRetry}
    >
      {services.length === 0 ? (
        <EmptyText text={t('dashboard.cards.emptyServices')} />
      ) : services.map((service) => (
        <Box key={service.serviceId} sx={rowSx}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {service.serviceName}
            </Typography>
            <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
              {t('dashboard.cards.topServicesPrevious', { count: service.previousBookingCount })}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: BRAND.text, whiteSpace: 'nowrap' }}>
            {t('dashboard.cards.topServicesBookings', { count: service.bookingCount })}
          </Typography>
        </Box>
      ))}
    </DashboardCard>
  );
};

export const TopTeamMembersCard = ({ members, currencyCode, isLoading, hasError, onRetry }) => {
  const { t } = useTranslation();
  const brand = useBrandPalette();
  const rankColors = [brand.accent, MEDAL_GREY, MEDAL_BRONZE];

  return (
    <DashboardCard
      id="dashboard-top-team-members"
      title={t('dashboard.cards.topTeamMembers')}
      subtitle={t('dashboard.cards.topTeamMembersSubtitle')}
      isLoading={isLoading}
      hasError={hasError}
      onRetry={onRetry}
    >
      {members.length === 0 ? (
        <EmptyText text={t('dashboard.cards.emptyTeamMembers')} />
      ) : members.map((member, index) => {
        const isPodium = index < PODIUM_SIZE;
        const rankColor = isPodium ? rankColors[index] : BRAND.textMuted;

        return (
          <Box key={member.teamMemberId} sx={rowSx}>
            <Avatar sx={{
              width: 32,
              height: 32,
              bgcolor: isPodium ? alpha(rankColor, 0.15) : alpha(MEDAL_GREY, 0.1),
              color: rankColor,
              fontSize: '0.8rem',
              fontWeight: 700,
            }}>
              {index + 1}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {member.teamMemberName}
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
                {t('dashboard.cards.topTeamMembersAppointments', { count: member.appointmentsCount })}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: BRAND.primaryText, whiteSpace: 'nowrap' }}>
              {formatCurrency(member.revenueReceived, currencyCode)}
            </Typography>
          </Box>
        );
      })}
    </DashboardCard>
  );
};

export const RecentActivityCard = ({ activities, currencyCode, isLoading, hasError, onRetry }) => {
  const { t } = useTranslation();

  return (
    <DashboardCard
      id="dashboard-recent-activity"
      title={t('dashboard.cards.recentActivity')}
      subtitle={t('dashboard.cards.recentActivitySubtitle')}
      isLoading={isLoading}
      hasError={hasError}
      onRetry={onRetry}
    >
      {activities.length === 0 ? (
        <EmptyText text={t('dashboard.cards.emptyActivity')} />
      ) : (
      <Box sx={RECENT_LIST_SX}>
      {activities.map((item) => (
        <Box key={item.id} sx={rowSx}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: BRAND.text, mb: 0.25 }}>
              {t(`dashboard.activityTypes.${normalizeEnumKey(item.activityType)}`, {
                customerName: item.customerName || '—',
                planName: item.planName || '—',
                defaultValue: item.activityType,
              })}
            </Typography>
            <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
              {[
                formatUtcDateTime(item.occurredAtUtc),
                item.amount != null ? formatCurrency(item.amount, currencyCode) : null,
                item.serviceNames,
              ].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        </Box>
      ))}
      </Box>
      )}
    </DashboardCard>
  );
};

export const RecentSalesCard = ({ sales, currencyCode, isLoading, hasError, onRetry }) => {
  const { t } = useTranslation();

  return (
    <DashboardCard
      id="dashboard-recent-sales"
      title={t('dashboard.cards.recentSales')}
      subtitle={t('dashboard.cards.recentSalesSubtitle')}
      isLoading={isLoading}
      hasError={hasError}
      onRetry={onRetry}
    >
      {sales.length === 0 ? (
        <EmptyText text={t('dashboard.cards.emptySales')} />
      ) : (
      <Box sx={RECENT_LIST_SX}>
      {sales.map((item) => (
        <Box key={`${item.appointmentId}-${item.paidAtUtc}`} sx={{ ...rowSx, justifyContent: 'space-between' }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: BRAND.text }}>
              {item.customerName}
            </Typography>
            <Typography variant="caption" sx={{ color: BRAND.textMuted }}>
              {formatUtcDateTime(item.paidAtUtc)} · {item.servicesSummary || '—'}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 700, color: BRAND.accentText, whiteSpace: 'nowrap' }}>
            {formatCurrency(item.amount, currencyCode)}
          </Typography>
        </Box>
      ))}
      </Box>
      )}
    </DashboardCard>
  );
};

